/**
 * Transaction Status Badge Component
 * Displays transaction status with animated visual indicators
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-4
 *
 * @description
 * Enhanced visual badge component for displaying transaction statuses with:
 * - Animated pulsing status indicator
 * - Color-coded statuses (Gray/Blue/Purple/Green/Teal)
 * - Multiple size variants (sm/md/lg)
 * - Full accessibility support (ARIA, dark mode)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <TransactionStatusBadge status="INITIALIZED" />
 *
 * // Large size with pulse animation
 * <TransactionStatusBadge status="QC_VERIFIED" size="lg" showPulse />
 *
 * // Custom styling
 * <TransactionStatusBadge status="APPROVED" className="ml-2" />
 * ```
 */

import { cn } from '@/lib/utils';

export type TransactionStatus = 'UP_COMING' | 'INITIALIZED' | 'QC_PENDING' | 'QC_VERIFIED' | 'APPROVED' | 'READY_FOR_PRODUCTION' | 'CONSUMED';
type Size = 'sm' | 'md' | 'lg';

export interface TransactionStatusBadgeProps {
    /**
     * Transaction status to display
     */
    status: TransactionStatus;

    /**
     * Visual size of the badge
     * @default 'md'
     */
    size?: Size;

    /**
     * Show animated pulse indicator
     * @default false
     */
    showPulse?: boolean;

    /**
     * Additional CSS classes
     */
    className?: string;
}

/**
 * Status configuration with colors, labels, and indicator colors
 */
const statusConfig: Record<TransactionStatus, {
    label: string;
    bgColor: string;
    textColor: string;
    darkBgColor: string;
    darkTextColor: string;
    indicatorColor: string;
    indicatorPulseColor: string;
}> = {
    UP_COMING: {
        label: 'Pending Approval',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        darkBgColor: 'dark:bg-orange-950/50',
        darkTextColor: 'dark:text-orange-300',
        indicatorColor: 'bg-orange-500',
        indicatorPulseColor: 'bg-orange-400'
    },
    INITIALIZED: {
        label: 'Initialized',
        bgColor: 'bg-gray-50',
        textColor: 'text-gray-700',
        darkBgColor: 'dark:bg-gray-950/50',
        darkTextColor: 'dark:text-gray-300',
        indicatorColor: 'bg-gray-500',
        indicatorPulseColor: 'bg-gray-400'
    },
    QC_PENDING: {
        label: 'Pending QC',
        bgColor: 'bg-amber-50',
        textColor: 'text-amber-700',
        darkBgColor: 'dark:bg-amber-950/50',
        darkTextColor: 'dark:text-amber-300',
        indicatorColor: 'bg-amber-500',
        indicatorPulseColor: 'bg-amber-400'
    },
    QC_VERIFIED: {
        label: 'QC Verified',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        darkBgColor: 'dark:bg-blue-950/50',
        darkTextColor: 'dark:text-blue-300',
        indicatorColor: 'bg-blue-500',
        indicatorPulseColor: 'bg-blue-400'
    },
    APPROVED: {
        label: 'Approved',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-700',
        darkBgColor: 'dark:bg-purple-950/50',
        darkTextColor: 'dark:text-purple-300',
        indicatorColor: 'bg-purple-500',
        indicatorPulseColor: 'bg-purple-400'
    },
    READY_FOR_PRODUCTION: {
        label: 'Ready',
        bgColor: 'bg-emerald-50',
        textColor: 'text-emerald-700',
        darkBgColor: 'dark:bg-emerald-950/50',
        darkTextColor: 'dark:text-emerald-300',
        indicatorColor: 'bg-emerald-500',
        indicatorPulseColor: 'bg-emerald-400'
    },
    CONSUMED: {
        label: 'Consumed',
        bgColor: 'bg-slate-50',
        textColor: 'text-slate-600',
        darkBgColor: 'dark:bg-slate-800/50',
        darkTextColor: 'dark:text-slate-300',
        indicatorColor: 'bg-slate-400',
        indicatorPulseColor: 'bg-slate-300'
    }
};

/**
 * Size configuration for badge dimensions
 */
const sizeConfig: Record<Size, {
    height: string;
    padding: string;
    fontSize: string;
    indicatorSize: string;
    gap: string;
}> = {
    sm: {
        height: 'h-5',
        padding: 'px-2',
        fontSize: 'text-xs',
        indicatorSize: 'h-1.5 w-1.5',
        gap: 'gap-1.5'
    },
    md: {
        height: 'h-6',
        padding: 'px-2.5',
        fontSize: 'text-sm',
        indicatorSize: 'h-2 w-2',
        gap: 'gap-2'
    },
    lg: {
        height: 'h-8',
        padding: 'px-3',
        fontSize: 'text-base',
        indicatorSize: 'h-2.5 w-2.5',
        gap: 'gap-2'
    }
};

/**
 * Animated Status Indicator Component
 */
function StatusIndicator({
    indicatorColor,
    indicatorPulseColor,
    size,
    animate = true
}: {
    indicatorColor: string;
    indicatorPulseColor: string;
    size: string;
    animate?: boolean;
}) {
    return (
        <span className={cn('relative flex', size)}>
            {animate && (
                <span
                    className={cn(
                        'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
                        indicatorPulseColor
                    )}
                />
            )}
            <span
                className={cn(
                    'relative inline-flex rounded-full',
                    size,
                    indicatorColor
                )}
            />
        </span>
    );
}

/**
 * Transaction Status Badge Component
 *
 * @implements Animated Status Indicator
 * @implements Status Colors (Amber/Blue/Purple/Emerald/Slate with dark mode)
 * @implements Size Variants (sm/md/lg)
 * @implements Accessibility (role, aria-label, dark mode)
 */
export function TransactionStatusBadge({
    status,
    size = 'md',
    showPulse = false,
    className
}: TransactionStatusBadgeProps) {
    const statusInfo = statusConfig[status];
    const sizeInfo = sizeConfig[size];

    const ariaLabel = `Transaction status: ${statusInfo.label}`;

    // Only animate for non-terminal statuses
    const shouldAnimate = showPulse && status !== 'CONSUMED';

    return (
        <span
            role="status"
            aria-label={ariaLabel}
            className={cn(
                // Base styles
                'inline-flex items-center justify-center rounded-full font-medium',
                'border border-transparent',
                'transition-all duration-200',

                // Size variant styles
                sizeInfo.height,
                sizeInfo.padding,
                sizeInfo.fontSize,
                sizeInfo.gap,

                // Status colors (light mode)
                statusInfo.bgColor,
                statusInfo.textColor,

                // Dark mode support
                statusInfo.darkBgColor,
                statusInfo.darkTextColor,

                // Custom classes
                className
            )}
        >
            <StatusIndicator
                indicatorColor={statusInfo.indicatorColor}
                indicatorPulseColor={statusInfo.indicatorPulseColor}
                size={sizeInfo.indicatorSize}
                animate={shouldAnimate}
            />
            {statusInfo.label}
        </span>
    );
}

export default TransactionStatusBadge;
